import React, { useState, useEffect } from 'react';
import { Form } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import debug from 'debug';

// Configuración de logs con namespace específico para el DropDownUserMenu
const logUserMenu = debug('rois:ui:user-menu');

// Habilitar logs solo en desarrollo
if (import.meta.env.DEV) {
  debug.enable('rois:ui*');
}

// GQL
import { client } from '../../../gql/client';
import { GET_TEAM_BY_ID } from '../../../gql/queries/users';

// Hooks
import { useAuth } from '../../../hooks/useAuth';

// Components
import { Typography } from '../Typography';

// Types
import { DropDownUserMenuProps, Roles } from './types';

// Styled Components
import {
  ButtonSession,
  Container,
  ContainerButtonPolicy,
  ContainerUser,
  HorizontalLine,
  LinkPolicy,
  PolicyVersionContainer,
  TextSection,
  EmailText,
  ContainerHeader,
  ContainerUserNameRole,
  ClearCacheButton,
  ChangePasswordButton,
} from './styles';

// Media
import { useIsLandscapeTablet } from '../../../hooks/useMediaBreakPoints';

// Utils
import { version } from '../../../../package.json';
import { ButtonUser } from '../../inputs/DropDown/DropDownSelectGroup/styles';

// Version Manager Hook
import { useVersionManager } from '../../../hooks/useVersionManager';

// Language Hook
import { useLanguage } from '../../../hooks/useLanguage';

// Components
import LanguageSelector from '../../inputs/LanguageSelector';

// Redux Actions
import { openModal } from '../../../state/actions/ui/modal';
import { modalIds } from '../../modals/SuperModal/types';

export const DropDownUserMenu = ({
  userName,
  userMail,
  userRole,
  onClose,
}: DropDownUserMenuProps): JSX.Element => {
  // `management` está aquí por la lista de equipos de más abajo
  // (`management:teamsList`). Este menú vive en el Header, o sea que se pinta en
  // TODAS las páginas, y hasta el 2026-09-07 el namespace no se cargaba en
  // ninguna que no fuera Gestión: el título salía como «teamsList» en crudo.
  // 🔴 Esta línea NO basta por sí sola, y quitarla de `ns` para dejar sólo esto
  // es peor que el bug: medido en navegador, al abrir el menú React suspende
  // sin boundary y la app entera cae a la pantalla de error. La carga real la
  // garantiza la lista `ns` de src/i18n/index.ts; declararlo también aquí sólo
  // hace visible la dependencia.
  const { t } = useTranslation(['common', 'errors', 'management']);
  const isLandscapeTablet = useIsLandscapeTablet();
  const { forceCacheClear } = useVersionManager();
  const [isClearing, setIsClearing] = useState(false);
  const dispatch = useDispatch();
  const { user } = useAuth();

  // ✅ M2M: State for user's teams
  const [userTeams, setUserTeams] = useState<{ id: string; name: string }[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  // ✅ M2M: Fetch user's teams on mount
  useEffect(() => {
    const fetchUserTeams = async () => {
      if (!user?.user?.teamIDs || user.user.teamIDs.length === 0) {
        setUserTeams([]);
        return;
      }

      setLoadingTeams(true);
      try {
        const teamQueries = user.user.teamIDs.map((teamId: string) =>
          client.query({
            query: GET_TEAM_BY_ID,
            variables: { id: teamId },
            fetchPolicy: 'cache-first',
          })
        );
        const teamResults = await Promise.all(teamQueries);
        const teams = teamResults
          .map((result) => result.data.team)
          .filter((team): team is { id: string; name: string } => !!team);
        setUserTeams(teams);
      } catch (error) {
        logUserMenu('❌ Error fetching user teams:', error);
        setUserTeams([]);
      } finally {
        setLoadingTeams(false);
      }
    };

    fetchUserTeams();
  }, [user?.user?.teamIDs]);

  const handleClearCache = async () => {
    if (isClearing) return;

    onClose?.(); // Cerrar el menú antes de mostrar el modal de confirmación

    // The versionManager modal handles the confirmation
    setIsClearing(true);
    try {
      // Forzar recarga de la traducción antes de usarla
      const clearMessage = t('errors:manualCacheClear');
      logUserMenu('🧹 Clearing cache with message:', clearMessage);
      await forceCacheClear(clearMessage);
      // The versionManager modal automatically handles the page reload
    } catch (error) {
      logUserMenu('❌ Error clearing cache:', error);
      setIsClearing(false);
    }
  };

  const handleOpenChangePasswordModal = () => {
    onClose?.(); // Cerrar el menú antes de abrir el modal
    dispatch(openModal(modalIds.CHANGE_PASSWORD_MODAL));
  };

  return (
    <Container>
      {isLandscapeTablet ? (
        <ContainerHeader>
          <ButtonUser initials={userName} />
          <ContainerUserNameRole>
            <Typography size="M" variant="semiBold">
              {userName}
            </Typography>
            <Typography size="XS">{Roles[userRole]}</Typography>
          </ContainerUserNameRole>
        </ContainerHeader>
      ) : (
        <Typography size="S">{Roles[userRole]}</Typography>
      )}
      <HorizontalLine />
      {/* CAMBIO: Mostrar email solo si existe (app-users), operarios sin email */}
      {userMail && (
        <>
      <ContainerUser>
        <Typography
          size={isLandscapeTablet ? 'XS' : 'S'}
          colorText="greyDark"
          variant="medium"
        >
          {t('labels.userInfo')}
        </Typography>
        <EmailText size={isLandscapeTablet ? 'XS' : 'S'} colorText="greyDark">
          {userMail}
        </EmailText>
      </ContainerUser>
      <HorizontalLine />
        </>
      )}

      {/* ✅ M2M: User's teams section */}
      {userTeams.length > 0 && (
        <>
          <ContainerUser>
            <Typography
              size={isLandscapeTablet ? 'XS' : 'S'}
              colorText="greyDark"
              variant="medium"
            >
              {t('management:teamsList')}
            </Typography>
            {loadingTeams ? (
              <Typography size="XS" colorText="greyDark">
                {t('common:loading')}...
              </Typography>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.5rem' }}>
                {userTeams.map((team) => (
                  <Typography key={team.id} size="XS" colorText="greyDark">
                    • {team.name}
                  </Typography>
                ))}
              </div>
            )}
          </ContainerUser>
          <HorizontalLine />
        </>
      )}

      {/* Language section */}
      <ContainerUser>
        <Typography
          size={isLandscapeTablet ? 'XS' : 'S'}
          colorText="greyDark"
          variant="medium"
        >
          {t('labels.language')}
        </Typography>
        <div style={{ marginTop: '0.5rem' }}>
          <LanguageSelector />
        </div>
      </ContainerUser>
      <HorizontalLine />

      {/* Settings section */}
      <ContainerUser>
        <Typography
          size={isLandscapeTablet ? 'XS' : 'S'}
          colorText="greyDark"
          variant="medium"
        >
          {t('labels.settings')}
        </Typography>
        <ChangePasswordButton
          text={t('buttons.changePassword')}
          variant="secondary"
          onClick={handleOpenChangePasswordModal}
        />
      </ContainerUser>
      <HorizontalLine />

      {/* Memory cache maintenance section */}
      <ContainerUser>
        <Typography
          size={isLandscapeTablet ? 'XS' : 'S'}
          colorText="greyDark"
          variant="medium"
        >
          {t('labels.maintenance')}
        </Typography>
        <ClearCacheButton
          text={isClearing ? t('buttons.clearing') : t('buttons.clearCache')}
          variant="secondary"
          onClick={handleClearCache}
          disabled={isClearing}
        />
        <Typography
          size="XS"
          colorText="greyDark"
          style={{ marginTop: '0.25rem' }}
        >
          {t('messages.clearCacheWarning')}
        </Typography>
      </ContainerUser>
      <HorizontalLine />

      <ContainerButtonPolicy>
        <Form action="logout" method="delete">
          <ButtonSession text={t('buttons.logout')} variant="secondary" />
        </Form>
        <PolicyVersionContainer>
          <LinkPolicy to={'#'}>
            <TextSection colorText="greyDark">
              {t('labels.privacyPolicy')}
            </TextSection>
          </LinkPolicy>
          <TextSection colorText="greyDark">
            {t('labels.version')}: {version}
          </TextSection>
        </PolicyVersionContainer>
      </ContainerButtonPolicy>
    </Container>
  );
};
